/**
 * sessionStorage 缓存契约（PUBLIC CONTRACT）
 *
 * 该缓存被 libs/blofin-nav 的 Footer 在不引入 blofin-utils 依赖的前提下读取，
 * 用于复用本服务已经决策出来的灰度结果，避免一次页面加载内重复请求 gray-config。
 *
 * 修改 CACHE_KEY、CACHE_TTL_MS 或 GrayConfig 字段时，必须同步更新：
 *   libs/blofin-nav/src/packages/Footer/hooks/useFooterAction.ts
 * 否则跨包的缓存读取会失效。
 */
const CACHE_KEY = 'chatbot_gray_config';
const CACHE_TTL_MS = 5 * 60 * 1000;

interface GrayConfig {
  hitGray: boolean;
  regionCode: string;
  wasLoggedIn: boolean;
  ts: number;
}

function readCache(): GrayConfig | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const config = JSON.parse(raw) as GrayConfig;
    if (Date.now() - config.ts > CACHE_TTL_MS) {
      sessionStorage.removeItem(CACHE_KEY);
      return null;
    }
    return config;
  } catch {
    return null;
  }
}

function writeCache(config: Omit<GrayConfig, 'ts'>) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ...config, ts: Date.now() }));
  } catch {
    // quota exceeded — ignore
  }
}

export function clearGrayCache() {
  try {
    sessionStorage.removeItem(CACHE_KEY);
  } catch {
    // ignore
  }
}

export async function fetchChatbotGrayConfig(
  baseUrl: string,
  getToken: () => string | null | undefined
): Promise<boolean> {
  const token = getToken();
  const isLoggedIn = !!token;

  const cached = readCache();
  if (cached && cached.wasLoggedIn === isLoggedIn) {
    return cached.hitGray;
  }

  const path = isLoggedIn
    ? '/uapi/v1/intelligent-bot/open/navigation/v2/gray-config'
    : '/uapi/v1/intelligent-bot/inner/navigation/v2/gray-config';

  const headers: Record<string, string> = {};
  if (token) headers['token'] = token;

  try {
    const res = await fetch(`${baseUrl}${path}`, { headers });
    const json = await res.json();

    if (String(json?.code) === '200' && json.data) {
      const hitGray = !!json.data.hitGray;
      writeCache({ hitGray, regionCode: json.data.regionCode ?? '', wasLoggedIn: isLoggedIn });
      return hitGray;
    }
  } catch (err) {
    console.warn('[chatbotGray] fetch failed, fallback to zendesk', err);
  }

  writeCache({ hitGray: false, regionCode: '', wasLoggedIn: isLoggedIn });
  return false;
}
